import { type FieldInputProps, useField } from 'formik';
import { type FocusEvent, type ReactNode, useCallback, useId } from 'react';

import { FormikField } from '@air/formik-field';
import { Input } from '@air/primitive-input';
import { VisuallyHidden } from '@air/primitive-visually-hidden';
import { cn } from '@air/utils-tailwind';

import {
  type DimensionBarFormData,
  type DimensionBarLabelPlacement,
  type DimensionBarProps,
  type DimensionMeasurement,
  type DimensionUnits,
} from '~/components/DimensionBar/DimensionBar';
import { DIMENSION_BAR_INPUT } from '~/constants/testIDs';

const DIMENSION_MEASUREMENT_LABELS: Record<DimensionMeasurement, string> = {
  height: 'Height',
  width: 'Width',
};

const DIMENSION_UNIT_DESCRIPTIONS: Record<DimensionUnits, string> = {
  px: 'pixels',
};

const DIMENSION_BAR_INSIDE_LABEL_TEST_IDS: Record<DimensionMeasurement, string> = {
  height: 'DIMENSION_BAR_HEIGHT_LABEL',
  width: 'DIMENSION_BAR_WIDTH_LABEL',
};

type DimensionBarInputWrapperProps = {
  children: ReactNode;
  dimensionMeasurement: DimensionMeasurement;
  dimensionUnits: DimensionUnits;
  onFocus: (isFocused: boolean) => void;
};

const DimensionBarInputWrapper = ({
  children,
  dimensionMeasurement,
  dimensionUnits,
  onFocus,
}: DimensionBarInputWrapperProps) => {
  const handleWrapperBlur = useCallback(() => onFocus(false), [onFocus]);
  const handleWrapperFocus = useCallback(() => onFocus(true), [onFocus]);

  return (
    // biome-ignore lint/a11y/noStaticElementInteractions: PLA-1724 — not interactive itself; listens for focus/blur bubbling from the nested input
    <div className="flex items-center" onFocus={handleWrapperFocus} onBlur={handleWrapperBlur}>
      <p className="font-bold text-12 uppercase">{dimensionMeasurement}</p>
      {children}
      <p className="ads:text-secondary text-16 text-gray-11">{dimensionUnits}</p>
    </div>
  );
};

type DimensionBarInputProps = {
  'data-testid'?: string;
  labelPlacement: DimensionBarLabelPlacement;
  onChange: FieldInputProps<DimensionBarFormData['height'] | DimensionBarFormData['width']>['onChange'];
  onFocus: (isFocused: boolean) => void;
} & Pick<DimensionBarInputWrapperProps, 'dimensionMeasurement' | 'dimensionUnits'> &
  Pick<DimensionBarProps, 'disabled'>;

type DimensionBarInsideLabelInputProps = Omit<DimensionBarInputProps, 'labelPlacement'>;

const DimensionBarInsideLabelInput = ({
  dimensionMeasurement,
  dimensionUnits,
  disabled,
  onChange,
  onFocus,
  'data-testid': testId,
}: DimensionBarInsideLabelInputProps) => {
  const inputId = useId();
  const unitDescriptionId = useId();
  const [field, meta] = useField<DimensionBarFormData['height'] | DimensionBarFormData['width']>(dimensionMeasurement);
  const { onBlur: onFormikBlur } = field;

  const handleInputBlur = useCallback(
    (event: FocusEvent<HTMLInputElement>) => {
      onFormikBlur(event);
      onFocus(false);
    },
    [onFocus, onFormikBlur],
  );
  const handleInputFocus = useCallback(() => onFocus(true), [onFocus]);

  return (
    <div
      className={cn(
        'flex h-10 min-w-0 flex-1 items-center gap-1 rounded-4 border ads:bg-input bg-surface-1 pr-2 pl-3 focus-within:ring-1',
        meta.error
          ? 'ads:border-error border-red-9 ads:focus-within:border-error focus-within:border-red-9 ads:focus-within:ring-error focus-within:ring-red-9'
          : 'ads:border-input border-gray-5 ads:focus-within:border-primary focus-within:border-blue-9 ads:focus-within:ring-focus focus-within:ring-blue-9',
        disabled && 'cursor-not-allowed',
      )}
    >
      <label
        className="shrink-0 ads:text-metadata text-16 text-gray-8"
        data-testid={DIMENSION_BAR_INSIDE_LABEL_TEST_IDS[dimensionMeasurement]}
        htmlFor={inputId}
      >
        {DIMENSION_MEASUREMENT_LABELS[dimensionMeasurement]}
      </label>
      <input
        {...field}
        aria-describedby={unitDescriptionId}
        aria-invalid={!!meta.error}
        className="min-w-0 flex-1 bg-transparent ads:text-primary text-16 text-gray-12 outline-none [appearance:textfield] disabled:cursor-not-allowed ads:disabled:text-placeholder disabled:text-gray-7 [&::-webkit-inner-spin-button]:appearance-none [&::-webkit-outer-spin-button]:appearance-none"
        data-testid={testId || 'DIMENSION_BAR_INPUT'}
        disabled={disabled}
        id={inputId}
        onBlur={handleInputBlur}
        onChange={onChange}
        onFocus={handleInputFocus}
        type="number"
      />
      <VisuallyHidden id={unitDescriptionId}>{DIMENSION_UNIT_DESCRIPTIONS[dimensionUnits]}</VisuallyHidden>
    </div>
  );
};

export const DimensionBarInput = ({
  dimensionMeasurement,
  dimensionUnits,
  disabled,
  labelPlacement,
  onChange,
  onFocus,
  'data-testid': testId,
}: DimensionBarInputProps) => {
  if (labelPlacement === 'inside') {
    return (
      <DimensionBarInsideLabelInput
        data-testid={testId}
        dimensionMeasurement={dimensionMeasurement}
        dimensionUnits={dimensionUnits}
        disabled={disabled}
        onChange={onChange}
        onFocus={onFocus}
      />
    );
  }

  return (
    <DimensionBarInputWrapper
      dimensionUnits={dimensionUnits}
      dimensionMeasurement={dimensionMeasurement}
      onFocus={onFocus}
    >
      <FormikField
        id={dimensionMeasurement}
        className="mx-1.5 w-16"
        isLabelHidden
        label={dimensionMeasurement}
        name={dimensionMeasurement}
        isErrorHidden
        onChange={onChange}
        component={
          <Input
            size="large"
            type="number"
            className="[appearance:textfield] [&::-webkit-inner-spin-button]:appearance-none [&::-webkit-outer-spin-button]:appearance-none"
            data-testid={testId || DIMENSION_BAR_INPUT}
            disabled={disabled}
          />
        }
      />
    </DimensionBarInputWrapper>
  );
};
